/**
 * Customers view — Phase 2.3.b.
 *
 * Owns the Customers tab: list of hotspot customers, search, and the
 * customer-detail dialog (devices, vouchers, sessions, reissue payments,
 * payment events, authmon snapshot/queues, voucher re-issue flow).
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `openDialog`, `debounce` — generic.
 *
 * Boot contract: `admin.html` loads this script with the other view
 * modules. The IIFE registers `window.adminViews.customers`. The view-
 * router calls `init()` on first open; `mount()` wires the search /
 * row-click / re-issue listeners at admin.js boot.
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports from `src/lib/format.js` + `src/lib/dialog.js`.
 */

import { escapeHtml, debounce, formatDateTimeFull, formatCurrency } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const adminAPI = api;
const API = api.routes;
const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let customersTbody, customerDlg, customerSearch, customerDevice,
      customerVouchers, customerSessions, customerReissues,
      customerPaymentEvents, customerAuthmon;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    customersTbody = document.getElementById('customersTbody');
    customerDlg = document.getElementById('customerDlg');
    customerSearch = document.getElementById('customerSearch');
    customerDevice = document.getElementById('customerDevice');
    customerVouchers = document.getElementById('customerVouchers');
    customerSessions = document.getElementById('customerSessions');
    customerReissues = document.getElementById('customerReissues');
    customerPaymentEvents = document.getElementById('customerPaymentEvents');
    customerAuthmon = document.getElementById('customerAuthmon');
  }

  // ---- State ----------------------------------------------------------
  let currentCustomerLookup = { phone: undefined, mac: undefined };

  function renderCustomers(items = []) {
    customersTbody.innerHTML = '';
    if (!items.length) { customersTbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">No customers</td></tr>'; return; }
    items.forEach((c, idx) => {
      const tr = document.createElement('tr');
      const lastAuth = c.last_auth_time ? formatDateTimeFull(c.last_auth_time) : '';
      tr.innerHTML = `
        <td>${idx + 1}</td>
        <td>${escapeHtml(c.phone_number || '')}</td>
        <td>${escapeHtml(c.customer_name || '')}</td>
        <td>${escapeHtml(c.mac_address || '')}</td>
        <td>${escapeHtml(c.last_seen_ap || '')}</td>
        <td>${escapeHtml(lastAuth)}</td>
        <td>
          <button class="btn" data-cact="detail" data-phone="${c.phone_number || ''}" data-mac="${c.mac_address || ''}">View</button>
        </td>`;
      customersTbody.appendChild(tr);
    });
  }

  async function openCustomerDetail({ phone, mac }) {
    customerDevice.innerHTML = '';
    customerVouchers.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>';
    customerSessions.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>';
    if (customerReissues) customerReissues.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">Loading…</td></tr>';
    if (customerPaymentEvents) customerPaymentEvents.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>';
    if (customerAuthmon) customerAuthmon.innerHTML = '<span style="color:var(--sub);">Loading…</span>';
    openDialog(customerDlg);
    currentCustomerLookup = { phone, mac };
    try {
      const detail = await adminAPI.fetch(API.customerDetail({ phone, mac }), { method: 'GET' });
      const d = detail.device || {};
      customerDevice.innerHTML = `
        <div class="form-grid" style="grid-template-columns: repeat(4, minmax(0,1fr));">
          <div class="field"><label>Phone</label><div>${escapeHtml(d.phone_number || '')}</div></div>
          <div class="field"><label>Name</label><div>${escapeHtml(d.customer_name || '')}</div></div>
          <div class="field"><label>MAC</label><div>${escapeHtml(d.mac_address || '')}</div></div>
          <div class="field"><label>Last AP</label><div>${escapeHtml(d.last_seen_ap || '')}</div></div>
          <div class="field"><label>Last Auth</label><div>${d.last_auth_time ? formatDateTimeFull(d.last_auth_time) : ''}</div></div>
        </div>`;

      const vouchers = Array.isArray(detail.vouchers) ? detail.vouchers : [];
      customerVouchers.innerHTML = '';
      if (!vouchers.length) customerVouchers.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">None</td></tr>';
      vouchers.forEach(v => {
        const status = String(v.status || (v.is_active || v.is_valid ? 'active' : 'inactive')).toLowerCase();
        const badgeClass = status === 'active' ? 'ok' : (status === 'not_started' ? 'off' : 'warn');
        const badgeLabel = status.replace(/_/g, ' ') || 'unknown';
        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td>${escapeHtml(v.code || '')}</td>
          <td>${escapeHtml(String(v.product_id || ''))}</td>
          <td>${v.valid_from ? formatDateTimeFull(v.valid_from) : ''}</td>
          <td>${v.valid_until ? formatDateTimeFull(v.valid_until) : ''}</td>
          <td><span class="badge ${badgeClass}">${escapeHtml(badgeLabel)}</span></td>`;
        customerVouchers.appendChild(tr);
      });

      const reissues = Array.isArray(detail.reissue_payments) ? detail.reissue_payments : [];
      if (customerReissues) {
        customerReissues.innerHTML = '';
        if (!reissues.length) customerReissues.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">None</td></tr>';
        reissues.forEach(p => {
          const tr = document.createElement('tr');
          tr.innerHTML = `
            <td>${escapeHtml(p.ref_id || '')}</td>
            <td>${p.amount != null ? formatCurrency(p.amount) : ''}</td>
            <td>${escapeHtml(String(p.product_id || ''))}</td>
            <td>${p.when_created ? formatDateTimeFull(p.when_created) : ''}</td>
            <td>${p.when_completed ? formatDateTimeFull(p.when_completed) : ''}</td>
            <td><button class="btn" data-reissue="${p.ref_id}">Re-issue</button></td>`;
          customerReissues.appendChild(tr);
        });
      }

      if (customerPaymentEvents) {
        const events = Array.isArray(detail.payment_events) ? detail.payment_events : [];
        customerPaymentEvents.innerHTML = '';
        if (!events.length) customerPaymentEvents.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">None</td></tr>';
        events.forEach(ev => {
          const when = ev.created_at ? formatDateTimeFull(ev.created_at) : '';
          const stage = String(ev.stage || '');
          const level = String(ev.level || '').toLowerCase();
          const badgeClass = level === 'error' ? 'warn' : (level === 'warn' || level === 'warning' ? 'off' : 'ok');
          const msg = ev.message || '';
          const tr = document.createElement('tr');
          tr.innerHTML = `
            <td>${escapeHtml(when)}</td>
            <td>${escapeHtml(String(ev.payment_ref || ''))}</td>
            <td>${escapeHtml(stage)}</td>
            <td><span class="badge ${badgeClass}">${escapeHtml(level || '')}</span></td>
            <td>${escapeHtml(String(msg))}</td>`;
          customerPaymentEvents.appendChild(tr);
        });
      }

      const sessions = Array.isArray(detail.sessions) ? detail.sessions : [];
      customerSessions.innerHTML = '';
      if (!sessions.length) customerSessions.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">None</td></tr>';
      sessions.forEach(s => {
        const status = String(s.activity_status || (s.is_active ? 'active' : (s.status || 'inactive'))).toLowerCase();
        const badgeClass = status === 'active' ? 'ok' : 'warn';
        const badgeLabel = status.replace(/_/g, ' ') || 'unknown';
        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td>${escapeHtml(s.ap_name || '')}</td>
          <td>${escapeHtml(s.ap_ip || '')}</td>
          <td>${s.start_time ? formatDateTimeFull(s.start_time) : ''}</td>
          <td>${escapeHtml(String(s.timeout || ''))}</td>
          <td>${s.expires_at ? formatDateTimeFull(s.expires_at) : ''}</td>
          <td><span class="badge ${badgeClass}">${escapeHtml(badgeLabel)}</span></td>`;
        customerSessions.appendChild(tr);
      });

      if (customerAuthmon) {
        const am = detail.authmon || null;
        const snap = am && am.snapshot ? am.snapshot : null;
        const queues = am && Array.isArray(am.queues) ? am.queues : [];
        if (!am) {
          customerAuthmon.textContent = 'None';
        } else {
          const parts = [];
          if (snap) {
            const updatedAt = snap.updated_at ? formatDateTimeFull(Number(snap.updated_at) * 1000) : '';
            const validUntil = snap.valid_until ? formatDateTimeFull(Number(snap.valid_until) * 1000) : '';
            parts.push(`<div><span class="badge ${snap.status === 'active' ? 'ok' : 'warn'}">${escapeHtml(String(snap.status || 'unknown'))}</span></div>`);
            parts.push(`<div>Voucher: ${escapeHtml(String(snap.voucher_code || ''))}</div>`);
            parts.push(`<div>AP: ${escapeHtml(String(snap.ap_id || snap.ap_ip || ''))}</div>`);
            parts.push(`<div>Valid until: ${escapeHtml(validUntil)}</div>`);
            parts.push(`<div>Updated: ${escapeHtml(updatedAt)}</div>`);
          } else {
            parts.push('<div style="color:var(--sub);">No snapshot</div>');
          }
          if (queues.length) {
            const q = queues.map(q => `${q.gatewayhash}${q.ip ? ` (${q.ip})` : ''}: ${q.in_ready ? 'ready' : ''}${q.in_ready && q.in_pending ? '+' : ''}${q.in_pending ? 'pending' : ''}`).join('<br/>');
            parts.push(`<div style="margin-top:8px;"><strong>Queues</strong><br/>${escapeHtml(q).replace(/&lt;br\/&gt;/g, '<br/>')}</div>`);
          } else {
            parts.push('<div style="margin-top:8px; color:var(--sub);">Queues: none</div>');
          }
          customerAuthmon.innerHTML = parts.join('');
        }
      }
    } catch (err) {
      customerDevice.innerHTML = '';
      customerVouchers.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">Failed to load</td></tr>';
      customerSessions.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">Failed to load</td></tr>';
      if (customerReissues) customerReissues.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">Failed to load</td></tr>';
      if (customerPaymentEvents) customerPaymentEvents.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">Failed to load</td></tr>';
      if (customerAuthmon) customerAuthmon.textContent = 'Failed to load';
      showFlash(err.message, 'error');
    }
  }

  async function loadCustomers(q = '') {
    const url = new URL(API.customers, window.location.origin);
    if (q) url.searchParams.set('q', q);
    try {
      const data = await adminAPI.fetch(url.pathname + url.search, { method: 'GET' });
      renderCustomers(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      renderCustomers([]);
      showFlash(err.message, 'error');
    }
  }

  let mounted = false;
  function mount() {
    if (mounted) return;
    mounted = true;
    resolveDom();
    if (customerSearch) {
      customerSearch.addEventListener('input', debounce(() => loadCustomers(customerSearch.value.trim()), 300));
    }
    if (customersTbody) {
      customersTbody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-cact]');
        if (!btn) return;
        const act = btn.dataset.cact;
        if (act === 'detail') {
          const phone = btn.dataset.phone || undefined;
          const mac = btn.dataset.mac || undefined;
          openCustomerDetail({ phone, mac });
        }
      });
    }
    if (customerReissues) {
      customerReissues.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-reissue]');
        if (!btn) return;
        const ref = btn.dataset.reissue;
        btn.disabled = true; btn.textContent = 'Re-issuing…';
        try {
          await adminAPI.fetch('/voucher/issue', { method: 'POST', body: JSON.stringify({ wave_reference: ref }) });
          showFlash('Voucher re-issued');
          openCustomerDetail(currentCustomerLookup);
        } catch (err) {
          showFlash(err.message, 'error');
        } finally {
          btn.disabled = false; btn.textContent = 'Re-issue';
        }
      });
    }
  }

export default {
  mount,
  init: () => { resolveDom(); loadCustomers(); },
};
